import { CloseOutlined, UploadOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Upload,
} from 'antd'
import { useWatch } from 'antd/es/form/Form'
import type { UploadFile } from 'antd/es/upload/interface'
import dayjs from 'dayjs'
import { BoosterProgramFormValues } from 'Modules'
import type { UploadRequestOption } from 'rc-upload/lib/interface'

import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import {
  BOOSTER_ENROLLMENT_PERIOD_MONTHS_MAX,
  BOOSTER_ENROLLMENT_PERIOD_UNIT_OPTIONS,
  BOOSTER_INCREASE_EVERY_UNIT_OPTIONS,
  BOOSTER_PARTICIPANT_LIMIT_TYPE_OPTIONS,
  BOOSTER_PROGRAM_FIELD_MAX_LENGTH,
  BOOSTER_PROGRAM_MESSAGES,
  BOOSTER_PROGRAM_TARGET_USER_OPTIONS,
  BOOSTER_PROGRAM_WHITELIST_MAX_SIZE_MB,
  BOOSTER_PROGRAM_WHITELIST_MIME_TYPES,
  BOOSTER_PROGRAM_WHITELIST_TEMPLATE_FILENAME,
  BOOSTER_PROGRAM_WHITELIST_TEMPLATE_HEADER,
  BOOSTER_PROGRAM_WHITELIST_TEMPLATE_SAMPLE_ADDRESSES,
} from 'constants/booster-program'

import { HorizontalRow } from './horizontal-row'

/**
 * Build the whitelist template CSV in-memory and trigger a browser download.
 * Generated client-side (no static asset) so it works identically on dev and prod
 * without depending on the server serving a file from /public.
 */
const downloadWhitelistTemplate = () => {
  const csv = [
    BOOSTER_PROGRAM_WHITELIST_TEMPLATE_HEADER,
    ...BOOSTER_PROGRAM_WHITELIST_TEMPLATE_SAMPLE_ADDRESSES,
  ].join('\n')
  // Prepend a BOM so Excel opens the UTF-8 CSV with correct encoding.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = BOOSTER_PROGRAM_WHITELIST_TEMPLATE_FILENAME
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Pull the last path segment from a URL for chip display. */
const extractFileName = (urlOrName?: string): string | undefined => {
  if (!urlOrName) return undefined
  return urlOrName.split('/').pop() || urlOrName
}

const MAX = BOOSTER_PROGRAM_FIELD_MAX_LENGTH

const EndDatePicker = () => {
  const form = Form.useFormInstance<BoosterProgramFormValues>()
  const periodStart = useWatch(['period', 0], form)
  return (
    <Form.Item
      name={['period', 1]}
      dependencies={[['period', 0]]}
      rules={[
        { required: true, message: 'End date is required' },
        ({ getFieldValue }) => ({
          validator(_, value) {
            const start = getFieldValue(['period', 0])
            if (!value || !start) return Promise.resolve()
            if (dayjs(value).isAfter(dayjs(start))) return Promise.resolve()
            return Promise.reject(new Error(BOOSTER_PROGRAM_MESSAGES.E05))
          },
        }),
      ]}
      style={{ marginBottom: 0 }}
    >
      <DatePicker
        showTime={{ format: 'HH:mm' }}
        format="DD/MM/YYYY HH:mm"
        placeholder="Select end date"
        style={{ width: 220 }}
        disabledDate={(current) =>
          current && (!periodStart || current < dayjs(periodStart).startOf('day'))
        }
      />
    </Form.Item>
  )
}

const WhitelistUpload = () => {
  const form = Form.useFormInstance<BoosterProgramFormValues>()
  const targetUsers = useWatch('targetUsers', form)
  const whitelistFileName = useWatch('whitelistFileName', form)

  if (targetUsers !== 'OTHERS') return null

  const beforeUpload = (file: File) => {
    if (!BOOSTER_PROGRAM_WHITELIST_MIME_TYPES.includes(file.type)) {
      message.error(BOOSTER_PROGRAM_MESSAGES.E03)
      return Upload.LIST_IGNORE
    }
    if (file.size / 1024 / 1024 > BOOSTER_PROGRAM_WHITELIST_MAX_SIZE_MB) {
      message.error(BOOSTER_PROGRAM_MESSAGES.E04)
      return Upload.LIST_IGNORE
    }
    return true
  }

  const handleCustomUpload = async ({ file, onSuccess }: UploadRequestOption) => {
    // Hold the raw file in form state; it's uploaded via the dedicated whitelist
    // endpoint once the program (and its id) exists — see the create submit flow.
    const selected = file as File
    form.setFieldsValue({ whitelistFile: selected, whitelistFileName: selected.name })
    onSuccess?.('ok')
  }

  const handleRemoveFile = () => {
    form.setFieldsValue({ whitelistFile: undefined, whitelistFileName: undefined })
  }

  const displayName = extractFileName(whitelistFileName)
  const fileList: UploadFile[] = displayName
    ? [{ uid: '-1', name: displayName, status: 'done' }]
    : []

  return (
    <div className="booster-program-create__upload">
      <Flex gap={12} align="center" wrap="wrap">
        <Upload
          customRequest={handleCustomUpload}
          beforeUpload={beforeUpload}
          showUploadList={false}
          accept=".csv,.xlsx,.xls"
        >
          <Button
            type="primary"
            icon={<UploadOutlined />}
            className="booster-program-create__primary-btn"
          >
            Upload File
          </Button>
        </Upload>
        {fileList.map((file) => (
          <span key={file.uid} className="booster-program-create__file-chip">
            {file.name}
            <CloseOutlined
              className="booster-program-create__file-chip-remove"
              onClick={handleRemoveFile}
            />
          </span>
        ))}
      </Flex>
      <div className="booster-program-create__hint">
        Upload list of users&apos; wallet addresses that can join booster program. File types: CSV,
        XLSX, XLS. Max size: {BOOSTER_PROGRAM_WHITELIST_MAX_SIZE_MB}MB
        <br />
        Template:{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            downloadWhitelistTemplate()
          }}
        >
          Template.csv
        </a>
      </div>
      {/* Hidden Form.Item so the uploaded file name is bound to form state and tracked by useWatch */}
      <Form.Item name="whitelistFileName" hidden>
        <Input />
      </Form.Item>
    </div>
  )
}

const LimitNumberField = () => {
  const form = Form.useFormInstance<BoosterProgramFormValues>()
  const participantLimitType = useWatch('participantLimitType', form)
  if (participantLimitType !== 'LIMITED') return null
  return (
    <Flex gap={8} align="center">
      <span className="booster-program-create__inline-label">Limit Number</span>
      <Form.Item
        name="participantLimit"
        rules={[
          { required: true, message: 'Limit Number is required' },
          {
            type: 'number',
            min: 1,
            message: BOOSTER_PROGRAM_MESSAGES.E01('Participant Limit'),
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        <InputNumber
          min={1}
          maxLength={MAX.limitNumber}
          placeholder="Input number"
          style={{ width: 140 }}
        />
      </Form.Item>
    </Flex>
  )
}

const EnrollmentPeriodField = () => {
  const form = Form.useFormInstance<BoosterProgramFormValues>()
  const unit = useWatch('enrollmentPeriodUnit', form) ?? 'months'
  const isMonths = unit === 'months'

  return (
    <Flex gap={12} align="center" wrap="wrap">
      <Form.Item
        name="enrollmentPeriodValue"
        dependencies={['enrollmentPeriodUnit']}
        rules={[
          { required: true, message: 'Enrollment period is required' },
          {
            type: 'number',
            min: 1,
            message: BOOSTER_PROGRAM_MESSAGES.E01('Enrollment period'),
          },
          ...(isMonths
            ? [
                {
                  type: 'number' as const,
                  max: BOOSTER_ENROLLMENT_PERIOD_MONTHS_MAX,
                  message: BOOSTER_PROGRAM_MESSAGES.E02('Enrollment period'),
                },
              ]
            : []),
        ]}
        style={{ marginBottom: 0 }}
      >
        <InputNumber
          min={1}
          max={isMonths ? BOOSTER_ENROLLMENT_PERIOD_MONTHS_MAX : undefined}
          maxLength={isMonths ? MAX.userEnrollmentPeriod : undefined}
          placeholder="Input number"
          style={{ width: 90 }}
        />
      </Form.Item>
      <Form.Item name="enrollmentPeriodUnit" style={{ marginBottom: 0 }}>
        <Select options={BOOSTER_ENROLLMENT_PERIOD_UNIT_OPTIONS} style={{ width: 120 }} />
      </Form.Item>
      <span className="booster-program-create__hint-inline">
        (from each user&apos;s enrollment date)
      </span>
    </Flex>
  )
}

export const ProgramSettings = () => {
  return (
    <div className="booster-program-create__section">
      <div className="booster-program-create__section-title">Program Settings</div>

      <HorizontalRow label="Program Title" required>
        <Form.Item
          name="title"
          rules={[{ required: true, message: 'Program Title is required' }]}
          style={{ marginBottom: 0, maxWidth: 360 }}
        >
          <Input placeholder="Enter program title" maxLength={MAX.programTitle} />
        </Form.Item>
      </HorizontalRow>

      <HorizontalRow label="Program Period" required>
        <Flex gap={8} align="center" wrap="wrap">
          <Form.Item
            name={['period', 0]}
            rules={[
              { required: true, message: 'Start date is required' },
              {
                validator: (_, value) => {
                  if (!value || !dayjs(value).isBefore(dayjs(), 'day')) return Promise.resolve()
                  return Promise.reject(new Error(BOOSTER_PROGRAM_MESSAGES.E09))
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Select start date"
              style={{ width: 220 }}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>
          <span className="booster-program-create__arrow">→</span>
          <EndDatePicker />
        </Flex>
      </HorizontalRow>

      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <MarkdownEditor placeholder="Enter program description" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Terms & Conditions"
            name="terms"
            rules={[{ required: true, message: 'Terms & Conditions is required' }]}
          >
            <MarkdownEditor placeholder="Enter terms & conditions" />
          </Form.Item>
        </Col>
      </Row>

      <HorizontalRow label="User Enrollment Period" required>
        <EnrollmentPeriodField />
      </HorizontalRow>

      <HorizontalRow label="Target Users" required>
        <Form.Item
          name="targetUsers"
          rules={[{ required: true, message: 'Target Users is required' }]}
          style={{ marginBottom: 0, maxWidth: 280 }}
        >
          <Select options={BOOSTER_PROGRAM_TARGET_USER_OPTIONS} />
        </Form.Item>
      </HorizontalRow>

      <WhitelistUpload />

      <HorizontalRow label="Participant Limit" required>
        <Form.Item
          name="participantLimitType"
          rules={[{ required: true, message: 'Participant Limit is required' }]}
          style={{ marginBottom: 0, maxWidth: 280 }}
        >
          <Select options={BOOSTER_PARTICIPANT_LIMIT_TYPE_OPTIONS} />
        </Form.Item>
      </HorizontalRow>

      <div className="booster-program-create__inline-row">
        <LimitNumberField />

        <Flex gap={8} align="center">
          <span className="booster-program-create__inline-label">Starting Fill Number</span>
          <Form.Item name="startingFillNumber" style={{ marginBottom: 0 }}>
            <InputNumber min={0} maxLength={MAX.startingFillNumber} style={{ width: 90 }} />
          </Form.Item>
        </Flex>

        <Flex gap={8} align="center">
          <span className="booster-program-create__inline-label">Increase by</span>
          <Form.Item name="increaseByPercent" style={{ marginBottom: 0 }}>
            <InputNumber min={0} maxLength={MAX.increaseByNumber} style={{ width: 80 }} />
          </Form.Item>
          <span className="booster-program-create__inline-label">% every</span>
          <Form.Item name="increaseEvery" style={{ marginBottom: 0 }}>
            <InputNumber min={0} maxLength={MAX.increaseByNumber} style={{ width: 80 }} />
          </Form.Item>
          <Form.Item name="increaseEveryUnit" style={{ marginBottom: 0 }}>
            <Select options={BOOSTER_INCREASE_EVERY_UNIT_OPTIONS} style={{ width: 110 }} />
          </Form.Item>
        </Flex>
      </div>
    </div>
  )
}
